/**
 * 2-wall corner-weld geometry — the r18 auto-resolve, hoisted so the PLAN cut and the
 * 3D solids derive the SAME patch (docs/MODELER.md field fixes, 2026-07-30).
 *
 * Two separate walls whose stored endpoints coincide leave a quarter NOTCH at the
 * outside corner — neither body covers it. The patch is the intersection of the two
 * walls' PROLONGED full-thickness strips: exactly the region a chained corner's miter
 * would cover. Field fixes baked in here:
 *
 * - ANGLE-AWARE prolongation: the original fixed length (tA + tB) falls short as the
 *   corner tightens — the miter tip distance grows ~t̄/sin(θ/2). Prolongation is now
 *   computed from the actual strip-face intersections, so the patch always contains
 *   the true miter region.
 * - MITER LIMIT (drafting convention, same idea as SVG miter-limit): past
 *   MITER_LIMIT × mean thickness the tip is cut square to the corner bisector — a
 *   BEVEL, never a spike.
 *
 * Same exactly-2-ends rule as always: consumers only call this for endpoint groups of
 * exactly two distinct walls — T's and 3+ nodes stay butt (behavior is a pure function
 * of endpoint coincidence counts, no stored join state).
 */
import {
  centerlineOffsetFt, bulgeToArc, angleOfPoint, arcTangentAt,
  lineCircleIntersect, circleCircleIntersect,
  type Path2D, type Pt2, type ResolvedElement,
} from '@aj/bim-kernel'
import { intersectClosed } from './clipper'

/**
 * CURVES round: an arm whose END EDGE is an arc carries its circle so the corner can
 * follow the curve — `r` is the CENTERLINE radius at the body end (|end − c|), `dirOut`
 * the angular direction of travel when prolonging OUTWARD along `u`, and `nRad` maps an
 * offset along `n` to a radius change (radius at offset f = r + f·nRad).
 */
export interface ArmArc {
  c: Pt2
  r: number
  dirOut: 1 | -1
  nRad: 1 | -1
}

export interface CornerArm {
  elementId: string
  el: ResolvedElement
  t: number
  end: Pt2            // BODY end center (stored endpoint + location-line delta)
  u: Pt2              // outward unit along the wall at this end (TRUE tangent for arcs)
  n: Pt2              // left normal of travel — the offset axis (exterior at sign·t/2)
  sign: 1 | -1        // flipped ? -1 : 1 (wallCoreLines' face-offset convention)
  /** present when the end edge is an arc — the strips + miter math follow the curve */
  arc?: ArmArc
}

/** endpoint-coincidence grouping tolerance (feet) — shared by the plan + 3D consumers */
export const CORNER_TOL = 0.05

/**
 * The arm's two end-face corner points (offset ±t/2 from the body end along the
 * arm's `n` axis) — exactly the vertices the arm's ISOLATED outline turns at. A
 * resolved 2-wall corner continues past both of them (the weld notch, or a plain
 * union when the pair shares a chunk), so they are never real silhouette corners
 * of the combined footprint — only the notch's own far mitered tip is. Shared by
 * the wall weld (pipeline.ts) and the foundation weld (footing.ts) to suppress
 * the false verticals both edge sources would otherwise draw there.
 */
export function armFacePts(arm: CornerArm): Pt2[] {
  const h = arm.t / 2
  return [
    [arm.end[0] + arm.n[0] * h, arm.end[1] + arm.n[1] * h],
    [arm.end[0] - arm.n[0] * h, arm.end[1] - arm.n[1] * h],
  ]
}

/** near-collinear guard: |cross(u_A, u_B)| below this = continuation/hairpin, no patch */
export const COLLINEAR_EPS = 0.05

/** tip distance beyond MITER_LIMIT × mean thickness bevels (drafting/SVG convention) */
export const MITER_LIMIT = 4

/**
 * The two end arms of an open wall path (stored endpoints; body-end centers offset by
 * the location line). Empty for closed/degenerate paths and zero-thickness walls.
 */
export function wallEndArms(el: ResolvedElement): { pt: Pt2; arm: CornerArm }[] {
  const stored = el.params.path as Path2D | null
  if (!stored?.nodes || stored.nodes.length < 2 || stored.closed) return []
  const t = el.assemblyThicknessFt
  if (t <= 0) return []
  const delta = centerlineOffsetFt(el.params.locationLine, el.params.flipped, el.assembly)
  const sign: 1 | -1 = el.params.flipped === true ? -1 : 1
  const nodes = stored.nodes
  const out: { pt: Pt2; arm: CornerArm }[] = []
  for (const endIdx of [0, nodes.length - 1]) {
    const pE = nodes[endIdx].pt
    const pN = nodes[endIdx === 0 ? 1 : nodes.length - 2].pt
    // CURVES round (11-curves-spike.md §3.4): the tangent at an arc end is
    // perpendicular to the radius there, NOT the chord to the far node
    const endArc = endIdx === 0
      ? bulgeToArc(pE, pN, nodes[0].bulge)
      : bulgeToArc(pN, pE, nodes[nodes.length - 2].bulge)
    let T: Pt2
    if (endArc) {
      // TRAVEL direction at pE — at node 0 it points INTO the wall, at the last node
      // OUT of it, matching the chord branch's convention exactly
      T = arcTangentAt(endArc, angleOfPoint(endArc, pE))
    } else {
      const tv: Pt2 = endIdx === 0 ? [pN[0] - pE[0], pN[1] - pE[1]] : [pE[0] - pN[0], pE[1] - pN[1]]
      const len = Math.hypot(tv[0], tv[1])
      if (len < 1e-9) continue
      T = [tv[0] / len, tv[1] / len]
    }
    const u: Pt2 = endIdx === 0 ? [-T[0], -T[1]] : T
    const n: Pt2 = [-T[1], T[0]]
    const end: Pt2 = [pE[0] + n[0] * delta, pE[1] + n[1] * delta]
    let arc: ArmArc | undefined
    if (endArc) {
      const r = Math.hypot(end[0] - endArc.c[0], end[1] - endArc.c[1])
      if (r > 1e-6) {
        const radial: Pt2 = [(end[0] - endArc.c[0]) / r, (end[1] - endArc.c[1]) / r]
        arc = {
          c: [endArc.c[0], endArc.c[1]],
          r,
          dirOut: (radial[0] * u[1] - radial[1] * u[0]) >= 0 ? 1 : -1,
          nRad: (n[0] * radial[0] + n[1] * radial[1]) >= 0 ? 1 : -1,
        }
      }
    }
    out.push({
      pt: pE,
      arm: { elementId: el.id, el, t, end, u, n, sign, ...(arc ? { arc } : {}) },
    })
  }
  return out
}

/**
 * Prolonged strip quad between offsets fa..fb along the arm's n, extended L beyond the
 * body end (and a hair behind it so unions/fills against the body never leave a seam
 * sliver).
 */
export function prolongedQuad(m: CornerArm, fa: number, fb: number, L: number): Pt2[] {
  const fLo = Math.min(fa, fb), fHi = Math.max(fa, fb)
  return [
    [m.end[0] - m.u[0] * 0.01 + m.n[0] * fLo, m.end[1] - m.u[1] * 0.01 + m.n[1] * fLo],
    [m.end[0] + m.u[0] * L + m.n[0] * fLo, m.end[1] + m.u[1] * L + m.n[1] * fLo],
    [m.end[0] + m.u[0] * L + m.n[0] * fHi, m.end[1] + m.u[1] * L + m.n[1] * fHi],
    [m.end[0] - m.u[0] * 0.01 + m.n[0] * fHi, m.end[1] - m.u[1] * 0.01 + m.n[1] * fHi],
  ]
}

// ---------- CURVES round: arc-aware strips (arc-arc corner patches, resolved Q3) ----------

/** angular span for a prolongation of arc length `len` (clamped to a half turn) */
const spanFor = (m: ArmArc, len: number): number => Math.min(len / Math.max(m.r, 0.05), Math.PI)

/** points of the arm's continuation arc at offset f, from `back` behind the end to
 *  `fwd` beyond it (arc lengths along the CENTERLINE circle) */
function armArcPoints(m: CornerArm, f: number, back: number, fwd: number): Pt2[] {
  const a = m.arc!
  const rF = Math.max(a.r + f * a.nRad, 0.02)
  const angEnd = Math.atan2(m.end[1] - a.c[1], m.end[0] - a.c[0])
  const ang0 = angEnd - a.dirOut * spanFor(a, back)
  const ang1 = angEnd + a.dirOut * spanFor(a, fwd)
  const steps = Math.max(8, Math.ceil(Math.abs(ang1 - ang0) / 0.08))
  const out: Pt2[] = []
  for (let s = 0; s <= steps; s++) {
    const ang = ang0 + ((ang1 - ang0) * s) / steps
    out.push([a.c[0] + rF * Math.cos(ang), a.c[1] + rF * Math.sin(ang)])
  }
  return out
}

/**
 * The prolonged strip between offsets fa..fb, extended L beyond the end — a plain quad
 * for straight ends (prolongedQuad exactly), a tessellated ANNULAR SECTOR for arc ends,
 * so a curved wall's corner patch and fill bands follow its curve instead of its
 * tangent line. Every corner consumer (patch intersection, plan fill bands) builds its
 * region from this one shape.
 */
export function prolongedStrip(m: CornerArm, fa: number, fb: number, L: number): Pt2[] {
  if (!m.arc) return prolongedQuad(m, fa, fb, L)
  const fLo = Math.min(fa, fb), fHi = Math.max(fa, fb)
  const lo = armArcPoints(m, fLo, 0.01, L)
  const hi = armArcPoints(m, fHi, 0.01, L)
  return [...lo, ...hi.reverse()]
}

/** The strip's centerline-at-offset-f polyline (batt-pattern orientation) — the arc
 *  analogue of the straight two-point mid line. */
export function prolongedMid(m: CornerArm, f: number, L: number): Pt2[] {
  if (!m.arc) {
    return [
      [m.end[0] - m.u[0] * 0.01 + m.n[0] * f, m.end[1] - m.u[1] * 0.01 + m.n[1] * f],
      [m.end[0] + m.u[0] * L + m.n[0] * f, m.end[1] + m.u[1] * L + m.n[1] * f],
    ]
  }
  return armArcPoints(m, f, 0.01, L)
}

/** the arm's centerline/face curve for intersection math: line for straight, circle
 *  (same center, radius shifted by f·nRad) for arcs */
interface ArmCurve { line?: { p: Pt2; d: Pt2 }; circle?: { c: Pt2; r: number } }
function faceCurve(m: CornerArm, f: number): ArmCurve {
  if (m.arc) return { circle: { c: m.arc.c, r: Math.max(m.arc.r + f * m.arc.nRad, 0.02) } }
  return { line: { p: [m.end[0] + m.n[0] * f, m.end[1] + m.n[1] * f], d: m.u } }
}
function armCurveIntersect(A: ArmCurve, B: ArmCurve, near: Pt2): Pt2 | null {
  let roots: Pt2[]
  if (A.line && B.line) {
    const c = A.line.d[0] * B.line.d[1] - A.line.d[1] * B.line.d[0]
    if (Math.abs(c) < 1e-9) return null
    roots = [lineX(A.line.p, A.line.d, B.line.p, B.line.d)]
  } else if (A.circle && B.circle) {
    roots = circleCircleIntersect(A.circle.c, A.circle.r, B.circle.c, B.circle.r)
  } else {
    const ln = A.line ?? B.line!
    const ci = A.circle ?? B.circle!
    roots = lineCircleIntersect(ln.p, ln.d, ci.c, ci.r)
  }
  let best: Pt2 | null = null
  let bd = Infinity
  for (const q of roots) {
    const d = Math.hypot(q[0] - near[0], q[1] - near[1])
    if (d < bd) { bd = d; best = q }
  }
  return best
}

/** arc length from the body end to `v` along the arm (projection for straight arms,
 *  centerline-circle arc length for arc arms; negative = behind the end) */
function armReachTo(m: CornerArm, v: Pt2): number {
  if (!m.arc) return (v[0] - m.end[0]) * m.u[0] + (v[1] - m.end[1]) * m.u[1]
  const a = m.arc
  const angEnd = Math.atan2(m.end[1] - a.c[1], m.end[0] - a.c[0])
  const angV = Math.atan2(v[1] - a.c[1], v[0] - a.c[0])
  let d = (angV - angEnd) * a.dirOut
  while (d > Math.PI) d -= Math.PI * 2
  while (d < -Math.PI) d += Math.PI * 2
  return d * a.r
}

const lineX = (p: Pt2, d: Pt2, q: Pt2, e: Pt2): Pt2 => {
  const c = d[0] * e[1] - d[1] * e[0]
  const tt = ((q[0] - p[0]) * e[1] - (q[1] - p[1]) * e[0]) / c
  return [p[0] + d[0] * tt, p[1] + d[1] * tt]
}

interface CornerMeasure {
  P0: Pt2         // centerline crossing
  tipFt: number   // true miter tip distance from P0 (farthest strip-face intersection)
  reachFt: number // miter-limited tip distance — what the corner actually extends to
  beveled: boolean
  L: number       // per-arm prolongation guaranteed to contain the whole strip overlap
  /**
   * INTERIOR CORNER round (2026-08-14, Matt's screenshot — inside corners drew no
   * line when the two walls don't share a chunk to union): the NEAREST of the same
   * 4 strip-face crossings — the mirror of the miter tip, on the inside of the
   * turn. When the walls share a group, this point is redundant (the unioned
   * outline already exposes it, more precisely, as a real vertex); when they
   * don't (different assemblies — no union ever runs between them), NOTHING else
   * draws a line there: each wall's own near-end corners are suppressed as
   * "covered by the notch," but the notch only ever fills the OUTSIDE gap. This
   * point is where the two walls' INTERIOR faces actually cross.
   */
  nearPt: Pt2
}

function measure(A: CornerArm, B: CornerArm): CornerMeasure | null {
  // the collinear guard stays on the TANGENTS — for arcs, u already IS the true
  // tangent (wallEndArms), so a tangent continuation between a line and its
  // osculating arc correctly reads as "no corner"
  if (Math.abs(A.u[0] * B.u[1] - A.u[1] * B.u[0]) < COLLINEAR_EPS) return null
  const near: Pt2 = [(A.end[0] + B.end[0]) / 2, (A.end[1] + B.end[1]) / 2]
  // centerline crossing — curve-true (CURVES round); tangent-line fallback when the
  // curves don't actually meet (short arcs whose circles miss)
  const P0 = armCurveIntersect(faceCurve(A, 0), faceCurve(B, 0), near)
    ?? lineX(A.end, A.u, B.end, B.u)
  const tBar = (A.t + B.t) / 2
  let tipFt = 0
  let nearFt = Infinity
  let nearPt: Pt2 = P0
  let L = 0
  for (const sa of [-1, 1]) {
    for (const sb of [-1, 1]) {
      // curve-true face crossing, tangent-line fallback per pair (containment-safe:
      // both are measured with the arm's own reach metric below)
      const v = armCurveIntersect(faceCurve(A, sa * A.t / 2), faceCurve(B, sb * B.t / 2), P0)
        ?? lineX(
          [A.end[0] + A.n[0] * sa * A.t / 2, A.end[1] + A.n[1] * sa * A.t / 2], A.u,
          [B.end[0] + B.n[0] * sb * B.t / 2, B.end[1] + B.n[1] * sb * B.t / 2], B.u,
        )
      const d = Math.hypot(v[0] - P0[0], v[1] - P0[1])
      tipFt = Math.max(tipFt, d)
      if (d < nearFt) { nearFt = d; nearPt = v }
      // exact per-arm prolongation to contain this vertex, from either body end
      L = Math.max(L, armReachTo(A, v), armReachTo(B, v))
    }
  }
  const beveled = tipFt > MITER_LIMIT * tBar
  return { P0, tipFt, reachFt: beveled ? MITER_LIMIT * tBar : tipFt, beveled, L: L + tBar, nearPt }
}

/**
 * The corner's INTERIOR meeting point — where the two walls' INTERIOR faces
 * actually cross (the mirror of the miter tip). Null for near-collinear
 * continuations, same guard as everywhere else in this module. Callers only
 * need this when the two walls WON'T end up unioned into one chunk (different
 * assemblies/levels/z-bands) — the union already exposes a real vertex there
 * on its own, more precisely, when they will.
 */
export function cornerInteriorPointFt(A: CornerArm, B: CornerArm): Pt2 | null {
  const m = measure(A, B)
  return m ? m.nearPt : null
}

/** Layer-line miter reach: the true tip distance when the corner MITERS. Null for
 *  near-collinear continuations AND past the miter limit — a beveled corner's layer
 *  lines butt instead of chasing an intersection outside the beveled patch. */
export function cornerMiterReachFt(A: CornerArm, B: CornerArm): number | null {
  const m = measure(A, B)
  return m && !m.beveled ? m.tipFt : null
}

export interface CornerPatchGeom {
  rings: Pt2[][]
  /** the prolongation used — plan fill bands must build their quads with the SAME L */
  L: number
  beveled: boolean
}

/**
 * The welded-corner patch: intersection of the two prolonged full-thickness strips,
 * bevel-clipped past the miter limit. Null for near-collinear continuations (they butt
 * face-to-face already) or empty intersections. Requires clipper WASM (intersectClosed).
 */
export function cornerPatchGeom(A: CornerArm, B: CornerArm): CornerPatchGeom | null {
  const m = measure(A, B)
  if (!m) return null
  // CURVES round: arc-ended arms intersect their true ANNULAR strips (prolongedStrip
  // degrades to the straight quad when no arc) — the patch follows the curves
  let rings = intersectClosed(
    [prolongedStrip(A, -A.t / 2, A.t / 2, m.L)],
    [prolongedStrip(B, -B.t / 2, B.t / 2, m.L)],
  ).filter((r) => r.length >= 3)
  if (!rings.length) return null
  if (m.beveled) {
    // cut the spike square to the outward bisector at the miter-limit distance —
    // keep { q : dot(q − P0, bis) ≤ reach }
    const bl = Math.hypot(A.u[0] + B.u[0], A.u[1] + B.u[1])
    if (bl > 1e-9) {
      const bis: Pt2 = [(A.u[0] + B.u[0]) / bl, (A.u[1] + B.u[1]) / bl]
      const dp: Pt2 = [-bis[1], bis[0]]
      const Pb: Pt2 = [m.P0[0] + bis[0] * m.reachFt, m.P0[1] + bis[1] * m.reachFt]
      const K = m.tipFt + m.L + 10
      const keep: Pt2[] = [
        [Pb[0] + dp[0] * K, Pb[1] + dp[1] * K],
        [Pb[0] - dp[0] * K, Pb[1] - dp[1] * K],
        [Pb[0] - dp[0] * K - bis[0] * K, Pb[1] - dp[1] * K - bis[1] * K],
        [Pb[0] + dp[0] * K - bis[0] * K, Pb[1] + dp[1] * K - bis[1] * K],
      ]
      rings = intersectClosed(rings, [keep]).filter((r) => r.length >= 3)
      if (!rings.length) return null
    }
  }
  return { rings, L: m.L, beveled: m.beveled }
}
